import { IFormStatus } from 'components/organisms/forms/types';
import { MutableRefObject } from 'react';
import {
  IContractSideMenuForm,
  IContractTemplateTreeItemForm,
  IDistributionSharesMutation,
} from 'services/templates/types';
import {
  IInfinitePageParams,
  IInfinitePageResponse,
  IVersion,
} from 'services/types';

export interface IContractBase {
  title: string;
  startDate: string;
  endDate: string | null;
}

export interface IContractDistributionShareMutation {
  salesChannelId: string;
  percentage: number;
}
export interface IContractDistributionMutation {
  defaultPercentage: number;
  position: number;
  title: string;
  shares: IContractDistributionShareMutation[];
}

export interface IContractTextAgreementMutation {
  position: number;
  title: string;
  defaultValue: string;
}

export interface IContractAgreementMutation {
  textAgreementRequest: IContractTextAgreementMutation | null;
}

export interface IContractSectionMutation {
  position: number;
  title: string;
  agreements: IContractAgreementMutation[];
  subsections: IContractSectionMutation[];
}

export interface IContractMutation extends IContractBase, IVersion {
  merchDistributionSection: IContractDistributionMutation | null;
  channelDistributionSection: IContractDistributionMutation | null;
  physicalDistributionSection: IContractDistributionMutation | null;
  digitalDistributionSection: IContractDistributionMutation | null;
  customSections: IContractSectionMutation[];
}

export interface ICreateContractFromTemplateForm
  extends Omit<IContractBase, 'title'> {
  contractTemplateId: string;
  withEndDate?: boolean;
}

export interface ICreateContractFromTemplate
  extends Omit<ICreateContractFromTemplateForm, 'withEndDate'> {
  labelId: string;
}

export interface ICreateContract extends IContractMutation {
  labelId: string;
}
export interface IUpdateContract extends IContractMutation {
  contractId: string;
}

export interface IContractDistributionShareResponse
  extends IDistributionSharesMutation {
  salesChannelName: string;
}
export interface IContractDistributionResponse {
  title: string;
  position: number;
  defaultSharePercentage: number;
  shares: IContractDistributionShareResponse[];
}

export interface IContractAgreementResponse
  extends Omit<IContractTextAgreementMutation, 'defaultValue'> {
  value: string;
}

export interface IContractCustomSectionResponse {
  position: number;
  title: string;
  agreement: IContractAgreementResponse[];
  customSections: IContractCustomSectionResponse[];
}

export interface IContractResponse extends IVersion {
  id: string;
  labelId: string;
  labelName: string;
  title: string;
  status: CONTRACT_STATUS;
  physicalDistributionSection: IContractDistributionResponse | null;
  digitalDistributionSection: IContractDistributionResponse | null;
  merchDistributionSection: IContractDistributionResponse | null;
  channelDistributionSection: IContractDistributionResponse | null;
  customSections: IContractCustomSectionResponse[];
  startDate: string;
  endDate: string | null;
}

export interface IContractsListSearch {
  contractTitle?: string;
  labelName?: string;
}

export interface IContractListParams
  extends IInfinitePageParams,
    IContractsListSearch {
  ShowOnlyActive?: boolean;
  ProductType?: PRODUCT_TYPE;
}

export interface IContractListItemResponse extends IContractBase {
  id: string;
  status: CONTRACT_STATUS;
  labelId: string;
  labelName?: string;
}

export interface IContractListResponse
  extends IInfinitePageResponse<IContractListItemResponse> {}

export enum CONTRACT_STATUS {
  unknown = -1,
  offer = 1,
  active = 2,
  withdraw = 3,
}

export interface IContractTemplateForm {
  items: IContractTemplateTreeItemForm[];
  activeId: string | null;
  overId: string | null;
  sideMenu: IContractSideMenuForm | null;
  offsetLeft: number;
}

export type IContractTemplateFlattenedTreeForm = {
  parentId: null | string;
  depth: number;
  index: number;
} & IContractTemplateTreeItemForm;

export type SensorContext = MutableRefObject<{
  items: IContractTemplateFlattenedTreeForm[];
  offset: number;
}>;

export interface IContractFormikStatus extends IFormStatus {
  namePrefix: string;
  indicator?: boolean;
  indentationWidth: number;
  maxDepth: number;
  minDepth: number;
  hideContract?: boolean;
}

export interface ITemplatesLightListItem {
  id: string;
  title?: string;
}

export interface IContractsToExpire {
  status: CONTRACT_STATUS;
  startDate: string;
  endDate: string;
  id: string;
  labelId: string;
  labelName: string;
  title: string;
}

export interface IContractTrack {
  id: string;
  mainTitle: {
    title: string;
    version: string | null;
    language: number;
  };
  isrc: string;
}
export interface IContractProduct {
  id: string;
  title: string;
  ean: string;
  productImageLink?: string;
  productType: {
    name: string;
    id: PRODUCT_TYPE;
  };
}

export enum PRODUCT_TYPE {
  Digital = 1,
  Physical = 2,
  Merch = 3,
  YouTube = 4,
}
export interface IContractVideo {
  id: string;
  channelId?: string;
  videoTitle: string;
  externalVideoId: string;
  videoImageLink: string;
  dateOfPublication: string;
  views: number;
  likesCount: number;
  dislikesCount: number;
}

export interface IContractBulkUpdate {
  currentContractId: string;
  newContractId: string;
  startDate: string;
}
